/**
 * Deep Linking Utilities
 *
 * Handles parsing and generation of deep links for notifications.
 * Format: /goto/<entity>/<id>
 *
 * Supports: card, board, user, job, job_run
 */

import StorageService from '@/utils/storage';
import { StorageKey } from '@/types/storage';
import { logger } from '@/utils/logger';

export enum DeepLinkEntity {
  CARD = 'card',
  BOARD = 'board',
  USER = 'user',
  JOB_RUN = 'job_run',
  COHORT_BOARD = 'cohort_board',
}

export interface DeepLink {
  entity: DeepLinkEntity;
  id: string;
  queryParams?: Record<string, string>;
}

/**
 * Parse a deep link URL into its components
 * Handles both absolute URLs (http://localhost:30179/goto/card/123)
 * and relative URLs (/goto/card/123)
 *
 * @param url - Full URL or pathname to parse
 * @returns DeepLink object or null if not a valid deep link
 */
export function parseDeepLink(
  url: string = window.location.href
): DeepLink | null {
  try {
    // Handle both absolute and relative URLs
    const urlObj = url.startsWith('http')
      ? new URL(url)
      : new URL(url, window.location.origin);
    const pathname = urlObj.pathname;

    // Match pattern: /goto/<entity>/<id>
    const match = pathname.match(/^\/goto\/([^/]+)\/([^/]+)$/);

    if (!match) {
      return null;
    }

    const [, entityStr, id] = match;

    // Validate entity type
    const entity = Object.values(DeepLinkEntity).find(e => e === entityStr);
    if (!entity) {
      logger.warn(`[DeepLink] Invalid entity type: ${entityStr}`);
      return null;
    }

    // Parse query params if present
    const queryParams: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    return {
      entity,
      id,
      queryParams:
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
    };
  } catch (error) {
    logger.error('[DeepLink] Error parsing deep link:', error as Error);
    return null;
  }
}

/**
 * Generate an absolute deep link URL for a given entity and ID
 * Used for creating notification landing URLs
 *
 * @param entity - Entity type (card, comment, board)
 * @param id - Entity ID
 * @param queryParams - Optional query parameters
 * @returns Absolute URL string
 */
export function generateDeepLink(
  entity: DeepLinkEntity,
  id: string,
  queryParams?: Record<string, string>
): string {
  const baseUrl = window.location.origin;
  const path = `/goto/${entity}/${id}`;

  if (queryParams && Object.keys(queryParams).length > 0) {
    const searchParams = new URLSearchParams(queryParams);
    return `${baseUrl}${path}?${searchParams.toString()}`;
  }

  return `${baseUrl}${path}`;
}

/**
 * Clean the deep link from the URL bar after navigation
 * Uses history.replaceState to avoid adding to browser history
 */
export function cleanDeepLinkUrl(): void {
  const currentPath = window.location.pathname;

  // Only clean if we're on a deep link URL
  if (currentPath.startsWith('/goto/')) {
    const baseUrl = window.location.origin;
    window.history.replaceState({}, '', baseUrl + '/');
    logger.debug('[DeepLink] Cleaned URL:', { url: baseUrl + '/' });
  }
}

/**
 * Set (or update) the browser URL bar to reflect the current navigation state.
 * Uses history.replaceState — does NOT add a browser history entry.
 * Preserves window.history.state so useNavigationHistory popstate handler
 * continues to recognize LearnBahn state entries.
 */
export function setNavigationUrl(
  entity: DeepLinkEntity,
  id: string,
  queryParams?: Record<string, string>
): void {
  const path = `/goto/${entity}/${id}`;
  const search =
    queryParams && Object.keys(queryParams).length > 0
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
  window.history.replaceState(window.history.state, '', path + search);
  logger.debug('[DeepLink] Set navigation URL:', { path, queryParams });
}

/**
 * Clear the navigation URL back to "/" when navigating to tabs with
 * no persistent entity URL (Velocity, Scheduled Jobs, etc.).
 * Only clears if the current path starts with /goto/ — no-op otherwise.
 */
export function clearNavigationUrl(): void {
  if (window.location.pathname.startsWith('/goto/')) {
    window.history.replaceState(window.history.state, '', '/');
    logger.debug('[DeepLink] Cleared navigation URL');
  }
}

/**
 * Store a deep link for post-login navigation.
 * Used when an unauthenticated user tries to access a deep link.
 */
export function storePendingDeepLink(deepLink: DeepLink): void {
  StorageService.set(StorageKey.PENDING_DEEP_LINK, deepLink);
  logger.debug('[DeepLink] Stored pending deep link:', { deepLink });
}

/**
 * Retrieve and clear a pending deep link.
 */
export function retrievePendingDeepLink(): DeepLink | null {
  const deepLink = StorageService.get<DeepLink>(StorageKey.PENDING_DEEP_LINK);
  if (!deepLink) {
    return null;
  }

  StorageService.remove(StorageKey.PENDING_DEEP_LINK);
  logger.debug('[DeepLink] Retrieved pending deep link:', { deepLink });
  return deepLink;
}
